import { DateService, PulseEnvironment } from '@pulse/browser-shared';
import { PulseStorage } from '@pulse/common';
import { Subject } from 'rxjs';

interface LastLocation {
    locationURL: string;
    timestamp: number;
}

/**
 * Tracks the location change in single page applications.
 */
export class LocationHistoryTracker {
    previousUrl: string;
    navigation = new Subject<string>();
    popStatePreviousUrl: string;
    lastLocationKey = 'last_location';
    internalLoadLimit = 2000;

    constructor(
        private env: PulseEnvironment,
        private tabStorage: PulseStorage,
        private dateService: DateService,
    ) {
        const lastLocation = this.getLastLocation();
        this.previousUrl = lastLocation?.locationURL || env.doc.referrer || '';
        this.popStatePreviousUrl = this.env.win.location.href;
        this.setLastLocation(this.env.win.location.href);

        addEventListener(
            'beforeunload',
            () => {
                this.setLastLocation();
            },
            { capture: true },
        );

        this.init();
    }

    private init() {
        if (typeof this.env.win.history?.pushState !== 'function') {
            return;
        }

        this.patch('back');
        this.patch('forward');
        this.patch('go');
        this.patch('pushState');
        this.patch('replaceState');

        this.env.win.addEventListener(
            'popstate',
            () => {
                const current = this.env.win.location.href;

                if (this.popStatePreviousUrl !== current) {
                    this.previousUrl = this.popStatePreviousUrl;
                    this.popStatePreviousUrl = current;
                    this.setLastLocation(current);
                    this.navigation.next(current);
                }
            },
            false,
        );
    }

    private patch(method: 'pushState' | 'replaceState' | 'back' | 'forward' | 'go') {
        const historyApi = this.env.win.history;
        const original: (...params: any[]) => any = historyApi[method].bind(historyApi);

        historyApi[method] = (...params: unknown[]) => {
            const previous = this.env.win.location.href;
            original.apply(historyApi, params);
            const current = this.env.win.location.href;

            if (previous !== current) {
                this.setLastLocation(current);
                this.popStatePreviousUrl = current;
                this.previousUrl = previous;
                this.navigation.next(current);
            }
        };
    }

    private setLastLocation(url: string = this.popStatePreviousUrl) {
        this.tabStorage.setJSON<LastLocation>(this.lastLocationKey, {
            locationURL: url,
            timestamp: this.dateService.now(),
        });
    }

    private getLastLocation(): LastLocation | null {
        let lastLocation = this.tabStorage.getJSON<LastLocation>(this.lastLocationKey);

        if (
            lastLocation?.timestamp &&
            this.dateService.hrtTimeOrigin - lastLocation?.timestamp < this.internalLoadLimit
        ) {
            lastLocation =
                this.env.win.location.href === lastLocation?.locationURL ? null : lastLocation;
        } else {
            lastLocation = null;
        }

        return lastLocation;
    }
}
